import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import { IArtistWithRoleMutation, IInfinitePageResponse } from 'services/types';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  ICreateMusicProductDigital,
  IUpdateMusicProductDigital,
} from './types/mutation';
import {
  IMusicProductDigitalArtistResponse,
  IMusicProductDigitalResponse,
  IMusicProductDigitalSearch,
  IMusicProductRelease,
  IMusicProductReleaseQuery,
} from './types/query';

export const useGetMusicProductsDigitalList = (
  params: IMusicProductDigitalSearch
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<IMusicProductDigitalResponse[], DefaultError>({
    queryKey: ['music-product-digital-list', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'MusicProductDigital/Search',
        params: {
          ...JSON.parse(JSON.stringify(params)),
          itemCount,
          page: pageParam,
        },
      });

      const data = await responseHandler<
        IInfinitePageResponse<IMusicProductDigitalResponse>
      >(response, {
        groupName: `useGetMusicProductsDigitalList - ${pageParam}`,
      });

      return infiniteHandler<IMusicProductDigitalResponse>(data);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetMusicProductDigital = (id: string, enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IMusicProductDigitalResponse, DefaultError>({
    queryKey: ['music-product-digital', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `MusicProductDigital/${id}`,
      });

      return responseHandler(response, {
        groupName: `useGetMusicProductDigital - ${id}`,
        notFoundPath: '/music-product-digital',
      });
    },
    enabled: enabled,
  });
};

export const usePostMusicProductDigital = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostMusicProductDigital'
  );
  return useMutation<
    IMusicProductDigitalResponse,
    DefaultError,
    ICreateMusicProductDigital
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: body.mainTitle.title ?? i18next.t('music-product-digital.what'),
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'MusicProductDigital',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePostMusicProductDigital',
        notFoundPath: '/music-product-digital',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: data.mainTitle.title ?? i18next.t('music-product-digital.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutMusicProductDigital = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePutMusicProductDigital'
  );
  return useMutation<
    IMusicProductDigitalResponse,
    DefaultError,
    IUpdateMusicProductDigital
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: body.mainTitle.title ?? i18next.t('music-product-digital.what'),
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'MusicProductDigital',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePutMusicProductDigital',
        notFoundPath: '/music-product-digital',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: data.mainTitle.title ?? i18next.t('music-product-digital.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteMusicProductDigital = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useDeleteMusicProductDigital'
  );
  return useMutation<{}, DefaultError>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.delete.loading', {
          what: i18next.t('music-product-digital.what'),
        })
      );
      const response = await apiClient({
        method: 'DELETE',
        url: `MusicProductDigital/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useDeleteMusicProductDigital',
        notFoundPath: '/music-product-digital',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.delete.success', {
          what: i18next.t('music-product-digital.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePostMusicProductDigtialFromPhysical = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostMusicProductDigtialFromPhysical'
  );
  return useMutation<string, DefaultError, string>({
    mutationFn: async musicProductPhysicalId => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('music-product-digital.what'),
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'MusicProductDigital/FromPhysical',
        body: { musicProductPhysicalId },
      });

      return responseHandler(response, {
        groupName: 'usePostMusicProductDigtialFromPhysical',
        notFoundPath: '/music-product-digital',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: i18next.t('music-product-digital.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePostMusicProductDigitalReplaceArtistDuplicates = () => {
  const { responseHandler } = useResponseHandler();
  return useMutation<
    IMusicProductDigitalArtistResponse[],
    DefaultError,
    IArtistWithRoleMutation[]
  >({
    mutationFn: async body => {
      const response = await apiClient({
        method: 'PUT',
        url: 'MusicProductDigital/ReplaceDuplicateArtists',
        body: { artistList: body },
      });
 
      return responseHandler(response, {
        groupName: 'usePostMusicProductDigitalReplaceArtistDuplicates',
        notFoundPath: '/music-product-digital',
      });
    },
  });
};
export const useGetMusicProductReleases = (
  query: IMusicProductReleaseQuery,
  enabled?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IMusicProductRelease[], DefaultError>({
    queryKey: ['music-product-digital-releases', query],
    queryFn: async () => {
      const response = await apiClient({
        method: 'GET',
        url: `MusicProductDigital/ReleasesCalendar`,
        params: query,
      });

      return responseHandler(response, {
        groupName: `useGetMusicProductReleases`,
      });
    },
    enabled: enabled,
  });
};
